/**
 * Custom hook for managing navbar styles based on selected menu scope
 * Provides reactive styling that updates when menu scope changes
 * Works on all routes and determines colors based on selected scope
 *
 * @module hooks/menuScope/useNavbarScopeStyles
 */
import { useMemo } from 'react';

// Custom Icons
import { CustomStoreIcon, CustomOrganizationIcon } from '@experimental-components/IconsComponents/CustomIcons';

// Navigation
import { NavigationType } from '@experimental-components/TopNavigationBar/types';
import useNavigation from 'hooks/navigation/useNavigation';

// Menu Scope
import { SCOPE_THEME_COLORS, SCOPE_FONT_COLORS } from 'utils/menuMaker/constants';

/**
 * Return type for useNavbarScopeStyles hook
 *
 * @interface NavbarScopeStylesReturn
 */
interface NavbarScopeStylesReturn {
  /**
   * Custom navbar style based on selected menu scope
   * Changes background color and font color according to selected scope
   */
  navbarCustomStyle: React.CSSProperties;

  /**
   * Icon component to render based on selected menu scope
   * Returns the component constructor, not the JSX element
   */
  NavbarIconComponent: React.ComponentType<{ style?: React.CSSProperties }> | null;

  /**
   * Icon style to apply to the navbar icon
   */
  iconStyle: React.CSSProperties;
}

/**
 * Hook for managing navbar styles based on navigation type
 *
 * Logic:
 * - If navigation is STORE → Show STORE colors and icon (#E8F5FF bg, #000000 font, StoreIcon)
 * - If navigation is STORE_DRAFT → Show ORGANIZATION colors and icon (#F04E4A bg, #FFFFFF font, OrganizationIcon)
 * - Defaults to STORE styling when no specific navigation type is determined
 * - Uses typeNavigation as single source of truth (synced with localStorage via useMenuScopeSync)
 *
 * @returns {NavbarScopeStylesReturn} Object with navbar style properties
 *
 * @example
 * const { navbarCustomStyle, NavbarIconComponent, iconStyle } = useNavbarScopeStyles();
 *
 * // Use in TopNavigationBar component
 * <TopNavigationBar
 *   customStyle={navbarCustomStyle}
 *   customIcon={NavbarIconComponent ? <NavbarIconComponent style={iconStyle} /> : null}
 * />
 */
export const useNavbarScopeStyles = (): NavbarScopeStylesReturn => {
  const { currentNavigation: typeNavigation } = useNavigation();

  // Determine mode based on navigation type
  const isStoreMode = typeNavigation === NavigationType.STORE;
  const isOrganizationMode = typeNavigation === NavigationType.STORE_DRAFT;

  /**
   * Custom navbar style based on navigation type
   * Uses theme constants for consistent styling across the app
   * Defaults to STORE styling when no specific mode is active
   */
  const navbarCustomStyle = useMemo(() => {
    if (isOrganizationMode) {
      return {
        backgroundColor: SCOPE_THEME_COLORS.ORGANIZATION,
        color: SCOPE_FONT_COLORS.ORGANIZATION,
      };
    }

    // Default to STORE styling (includes isStoreMode and fallback cases)
    return {
      backgroundColor: SCOPE_THEME_COLORS.STORE,
      color: SCOPE_FONT_COLORS.STORE,
    };
  }, [isStoreMode, isOrganizationMode]);

  /**
   * Icon component based on navigation type
   * - STORE navigation (or default) → CustomStoreIcon
   * - STORE_DRAFT navigation → CustomOrganizationIcon
   */
  const NavbarIconComponent = useMemo(() => {
    if (isOrganizationMode) {
      return CustomOrganizationIcon;
    }

    // Default to STORE icon (includes isStoreMode and fallback cases)
    return CustomStoreIcon;
  }, [isStoreMode, isOrganizationMode]);

  /**
   * Icon style to apply when rendering the icon
   * Color matches the font color of the respective navigation theme
   * Defaults to STORE font color when no specific mode is active
   */
  const iconStyle = useMemo(() => {
    if (isOrganizationMode) {
      return {
        color: SCOPE_FONT_COLORS.ORGANIZATION,
        display: 'flex',
      };
    }

    // Default to STORE styling (includes isStoreMode and fallback cases)
    return {
      color: SCOPE_FONT_COLORS.STORE,
      display: 'flex',
    };
  }, [isStoreMode, isOrganizationMode]);

  return {
    navbarCustomStyle,
    NavbarIconComponent,
    iconStyle,
  };
};

export default useNavbarScopeStyles;
